import { useState } from "react";
import { useSearchParams } from "react-router";
import {
  useActors, useApproveSkillVersion, useArchiveSkill, useFetchSkills, useMe, useRefetchSkill, useRejectSkillVersion,
  useSetSkillAgents, useSkill, useSkills, useSkillVersion,
} from "../api.ts";
import { Action, Page, PageHeader } from "../calm.tsx";
import { SkillAddForm } from "../components/skills/SkillAddForm.tsx";
import { AgentPicker, SkillLibrary, SkillRow } from "../components/skills/SkillLibrary.tsx";
import { SkillReview } from "../components/skills/SkillReview.tsx";
import type { SkillFolderDTO } from "../components/skills/skills.ts";
import { officeActors } from "../roster.ts";
import { ErrorNote } from "../ui.tsx";

function SkillAgents({ skillId, isAdmin }: { skillId: string; isAdmin: boolean }) {
  const detail = useSkill(skillId);
  const actors = useActors().data ?? [];
  const save = useSetSkillAgents();
  const agents = officeActors(actors).filter((a) => a.kind === "agent").map((a) => ({ id: a.id, name: a.name }));
  const [, setSearch] = useSearchParams();
  if (!detail.data) return <p className="font-mono text-[12px] text-ink-3">loading…</p>;
  return (
    <>
      <ul className="mb-4 flex flex-wrap gap-2" data-testid="skill-versions" aria-label="Versions">
        {detail.data.versions.map((v) => (
          <li key={v.id}>
            <button type="button" onClick={() => setSearch({ review: v.id })} className="rounded-full bg-ink/[0.06] px-3 py-1 font-mono text-[11.5px]" data-testid={`skill-version-${v.version}`}>
              v{v.version} · {v.status}
            </button>
          </li>
        ))}
      </ul>
      <AgentPicker agents={agents} selected={detail.data.agentIds} isAdmin={isAdmin} saving={save.isPending} onSave={(agentIds) => save.mutate({ id: skillId, agentIds })} />
      <ErrorNote error={save.error} />
    </>
  );
}

function ReviewPane({ versionId, isAdmin, onClose }: { versionId: string; isAdmin: boolean; onClose: () => void }) {
  const version = useSkillVersion(versionId);
  const approve = useApproveSkillVersion();
  const reject = useRejectSkillVersion();
  const [sentForRisk, setSentForRisk] = useState(false);
  if (version.isPending) return <p className="font-mono text-[12px] text-ink-3">Reading version…</p>;
  if (!version.data) return <ErrorNote error={version.error ?? new Error("Version not found.")} />;
  return (
    <div className="space-y-4">
      <Action onClick={onClose} testId="skill-review-close">← Back to library</Action>
      <SkillReview
        // A fresh verdict state per version.
        key={version.data.id}
        version={version.data}
        isAdmin={isAdmin}
        locked={sentForRisk}
        busy={approve.isPending || reject.isPending}
        onApprove={(input) => approve.mutate({ id: versionId, ...input }, { onSuccess: (res) => {
          // A version with high-risk items stays pending behind a risk_acceptance decision: say so instead of closing silently.
          if ((res as { status?: string } | null)?.status === "pending") setSentForRisk(true);
          else onClose();
        } })}
        onReject={(reason) => reject.mutate({ id: versionId, reason }, { onSuccess: onClose })}
      />
      {sentForRisk && <p className="rounded-[14px] bg-signal-soft p-3 text-[13px]" role="status" data-testid="skill-risk-sent">Sent for risk acceptance. The version stays pending until that decision is approved.</p>}
      <ErrorNote error={approve.error ?? reject.error} />
    </div>
  );
}

export function SkillsPage() {
  const [params, setParams] = useSearchParams();
  const isAdmin = useMe().data?.access === "admin";
  const skills = useSkills();
  const fetchSkills = useFetchSkills();
  const refetch = useRefetchSkill();
  const archive = useArchiveSkill();
  const [adding, setAdding] = useState(params.has("add"));
  const [choose, setChoose] = useState<SkillFolderDTO[] | undefined>();
  const [expanded, setExpanded] = useState<string | null>(null);
  const review = params.get("review");
  const closeReview = () => setParams((p) => { const n = new URLSearchParams(p); n.delete("review"); return n; });
  const closeAdd = () => {
    setAdding(false);
    setChoose(undefined);
    fetchSkills.reset();
    setParams((p) => { const n = new URLSearchParams(p); n.delete("add"); return n; });
  };
  const active = (skills.data ?? []).filter((s) => s.archivedAt === null);

  if (review) return <Page wide><PageHeader crumbs={[{ label: "Skills", to: "/skills" }, { label: "Review" }]} title="Review skill" /><div className="mt-6"><ReviewPane versionId={review} isAdmin={isAdmin} onClose={closeReview} /></div></Page>;

  return (
    <Page wide>
      <PageHeader
        title="Skills"
        meta={<span className="text-ink-2">Claude Code skills the admin has vetted. Nothing updates on its own.</span>}
        actions={isAdmin && !adding ? <Action primary onClick={() => setAdding(true)} testId="skill-add">Add skill</Action> : undefined}
      />
      <div className="mt-6 space-y-6">
        {isAdmin && adding && (
          <SkillAddForm
            initialSource={params.get("add") ?? ""}
            choose={choose}
            onSourceChange={() => setChoose(undefined)}
            busy={fetchSkills.isPending}
            error={fetchSkills.error}
            onCancel={closeAdd}
            onFetch={(input) => fetchSkills.mutate(input, { onSuccess: (res) => (res.choose?.length ? setChoose(res.choose) : closeAdd()) })}
          />
        )}
        <ErrorNote error={skills.error ?? refetch.error ?? archive.error} />
        {skills.isPending ? (
          <p className="font-mono text-[12px] text-ink-3">loading…</p>
        ) : skills.isError ? null : (
          <SkillLibrary skills={active}>
            {active.map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                isAdmin={isAdmin}
                expanded={expanded === skill.id}
                busy={refetch.isPending || archive.isPending}
                onToggleAgents={() => setExpanded((cur) => (cur === skill.id ? null : skill.id))}
                onReview={(id) => setParams((p) => { const n = new URLSearchParams(p); n.set("review", id); return n; })}
                onRefetch={() => refetch.mutate(skill.id)}
                onArchive={() => {
                  if (window.confirm(`Archive ${skill.name}? It leaves every agent's next run.`)) archive.mutate(skill.id);
                }}
              >
                <SkillAgents skillId={skill.id} isAdmin={isAdmin} />
              </SkillRow>
            ))}
          </SkillLibrary>
        )}
      </div>
    </Page>
  );
}
